// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/flags.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * WebSocket API Flags and Keys
 * Centralized constants for all WebSocket communication
 */

// View Center Control Flags
export const VIEW_CENTER_UPDATE_FLAG = 'view_center_update';
export const VIEW_CENTER_LONGITUDE_KEY = 'view_center_longitude_deg';
export const VIEW_CENTER_LATITUDE_KEY = 'view_center_latitude_deg';

// Canvas Control Flags
export const CANVAS_CONTROL_FLAG = 'canvas_control';
export const CANVAS_STATE_FLAG = 'canvas_state';
export const CANVAS_X_KEY = 'canvas_x';
export const CANVAS_Y_KEY = 'canvas_y';
export const CANVAS_ZOOM_KEY = 'canvas_zoom';

// Media Control Flags
export const MEDIA_PLAY_ACTION = 'play';
export const MEDIA_PAUSE_ACTION = 'pause';
export const MEDIA_STOP_ACTION = 'stop';
export const MEDIA_SEEK_ACTION = 'seek';
export const MEDIA_VOLUME_ACTION = 'volume';
export const MEDIA_TIME_KEY = 'time';
export const MEDIA_VOLUME_KEY = 'volume';
export const MEDIA_SPEED_ACTION = 'speed';
export const MEDIA_SPEED_KEY = 'speed';

// Auto Rotation Control Flags
export const ROTATION_CONTROL_FLAG = 'rotation_control';
export const ROTATION_ENABLED_KEY = 'rotation_enabled';
export const ROTATION_SPEED_KEY = 'rotation_speed';
export const ROTATION_STATE_FLAG = 'rotation_state';

// Playlist Control Flags
export const PLAYLIST_CONTROL_FLAG = 'playlist_control';
export const PLAYLIST_STATE_FLAG = 'playlist_state';
export const PLAYLIST_COMMAND_KEY = 'playlist_command';
export const PLAYLIST_STATE_KEY = 'playlist_state_payload';
export const PLAYLIST_ID_KEY = 'playlist_id';
export const PLAYLIST_ITEM_INDEX_KEY = 'playlist_item_index';
export const PLAYLIST_AUTOPLAY_KEY = 'playlist_autoplay';
export const PLAYLIST_DATA_KEY = 'playlist_data';

// Dataset Control Flags
export const DATASET_CONTROL_FLAG = 'dataset_control';
export const DATASET_COMMAND_KEY = 'dataset_command';
export const DATASET_CREATE_VIMEO_COMMAND = 'create_vimeo_dataset';

// Asset Catalog Flags
export const ASSET_CONTROL_FLAG = 'asset_control';
export const ASSET_STATE_FLAG = 'asset_state';
export const ASSET_COMMAND_KEY = 'asset_command';
export const ASSET_STATE_KEY = 'asset_state_payload';
export const ASSET_LIST_KEY = 'asset_list';
export const ASSET_SCAN_COMMAND = 'scan_assets';
export const ASSET_SELECT_COMMAND = 'select_asset';
export const ASSET_SELECTED_ID_KEY = 'asset_selected_id';
export const ASSET_SYNC_CURRENT_COMMAND = 'sync_current_asset';
export const ASSET_CATEGORY_FILTER_COMMAND = 'set_category_filter';
export const ASSET_CATEGORY_FILTER_KEY = 'category_filter';

// Overlay Flags (transparent layers on top of the globe)
export const OVERLAY_CONTROL_FLAG = 'overlay_control';
export const OVERLAY_STATE_FLAG = 'overlay_state';
export const OVERLAY_COMMAND_KEY = 'overlay_command';
export const OVERLAY_ACTIVE_IDS_KEY = 'overlay_active_ids';
export const OVERLAY_SETTINGS_KEY = 'overlay_settings';
export const OVERLAY_SET_ACTIVE_COMMAND = 'set_overlays';
export const OVERLAY_CLEAR_COMMAND = 'clear_overlays';

// Measurements placed on the globe (pins, transect lines, boxes). Broadcast so
// every surface — the sphere output, the controller, a kiosk — shows the same
// measurements. Only committed measurements and their visibility travel: the
// active tool and any half-drawn shape stay local to the surface holding the
// pointer, so one operator's sync cannot hijack another's input.
export const MEASUREMENT_STATE_FLAG = 'measurement_state';
export const MEASUREMENT_ITEMS_KEY = 'measurement_items';
export const MEASUREMENT_VISIBLE_KEY = 'measurement_visible';

// Value-encoded (data-luma) presentation state. The renderer is the only
// surface that decodes, so it publishes what the decode currently looks like
// (palette, stretch, range, units) and a remote surface drives the same
// controls back through the `luma.*` command actions, never decoding itself.
// The palette row travels as base64 (256 RGBA entries, 1 KB) so a colorbar can
// be drawn where the frame cannot be sampled.
export const LUMA_STATE_FLAG = 'luma_state';
export const LUMA_STATE_KEY = 'luma_state_payload';
export const LUMA_PALETTE_LUT_KEY = 'luma_palette_lut';
export const LUMA_DATASET_TITLE_KEY = 'luma_dataset_title';

// Measurement charts (a probe's history, a transect's distance-against-time)
// are hundreds of slots each and are rewritten many times a second, so they
// stay OUT of `measurement_state` and go out on their own flag at a gentle
// cadence for surfaces that draw them. Hovmöller cells travel as base64
// float32 (see shared/measurement-transport.ts).
export const MEASUREMENT_CHARTS_STATE_FLAG = 'measurement_charts_state';
export const MEASUREMENT_SERIES_KEY = 'measurement_series';
export const MEASUREMENT_HOVMOLLER_KEY = 'measurement_hovmoller';
/** Rides `measurement_state`: whether the graphs are mirrored onto the output. */
export const MEASUREMENT_ANALYSIS_OUTPUT_KEY = 'measurement_analysis_output';
/**
 * A remote operator's live pointer (crosshair, half-drawn shape) while they
 * aim on a controller, so the sphere shows where the pin will land before
 * they lift. Sent while the pointer moves, null on release.
 */
export const MEASUREMENT_POINTER_STATE_FLAG = 'measurement_pointer_state';
export const MEASUREMENT_POINTER_KEY = 'measurement_pointer';

// Ground layer (the reference surface under transparent datasets). The list
// and the stored choice live in the main process, so a remote client asks the
// server: `list` answers the asker, `set` stores and answers everyone.
export const GROUND_LAYER_CONTROL_FLAG = 'ground_layer_control';
export const GROUND_LAYER_COMMAND_KEY = 'ground_layer_command';
export const GROUND_LAYER_ID_KEY = 'ground_layer_id';
export const GROUND_LAYER_STATE_FLAG = 'ground_layer_state';
export const GROUND_LAYER_BASEMAPS_KEY = 'ground_layer_basemaps';
export const GROUND_LAYER_SELECTED_ID_KEY = 'ground_layer_selected_id';
export const GROUND_LAYER_EFFECTIVE_ID_KEY = 'ground_layer_effective_id';

// Realtime composition state (master-clock alpha overlays)
export const RT_COMPOSITION_STATE_FLAG = 'rt_composition_state';
export const RT_ACTIVE_LAYER_IDS_KEY = 'rt_active_layer_ids';
export const RT_LAYER_SETTINGS_KEY = 'rt_layer_settings';
export const RT_MASTER_LAYER_ID_KEY = 'rt_master_layer_id';
export const RT_GLOBAL_TIME_KEY = 'rt_global_time';
export const RT_CANDIDATE_IDS_KEY = 'rt_candidate_ids';

// Display settings state (time overlay + spherical legend controls)
export const DISPLAY_SETTINGS_STATE_FLAG = 'display_settings_state';
export const TIME_OVERLAY_ENABLED_KEY = 'time_overlay_enabled';
export const TIME_OVERLAY_SIZE_DEG_KEY = 'time_overlay_size_deg';
export const TIME_OVERLAY_FORMAT_KEY = 'time_overlay_format';
export const TIME_OVERLAY_LATITUDE_KEY = 'time_overlay_latitude';
export const TIME_OVERLAY_START_LON_KEY = 'time_overlay_start_lon';
export const LEGEND_4COPY_ENABLED_KEY = 'legend_4copy_enabled';
export const LEGEND_SIZE_DEG_KEY = 'legend_size_deg';
export const LEGEND_TARGET_WIDTH_PERCENT_KEY = 'legend_target_width_percent';
export const LEGEND_LATITUDE_KEY = 'legend_latitude';
export const LEGEND_START_LON_KEY = 'legend_start_lon';
export const SPHERE_OUTPUT_RESOLUTION_WIDTH_KEY = 'sphere_output_resolution_width';

// Location bookmark state
export const LOCATION_BOOKMARKS_STATE_FLAG = 'location_bookmarks_state';
export const LOCATION_BOOKMARKS_KEY = 'location_bookmarks';

// ---- Overlay Rendering Performance ----
/**
 * Prewarm N overlay textures after the overlays catalog becomes available.
 * This reduces the "first click" hitch by decoding + uploading the first few overlays ahead of time.
 * Set to 0 to disable.
 */
export const OVERLAY_PREWARM_COUNT = 2;

// DSA Layer Flags (multi-layer dataset controls)
export const DSA_LAYER_STATE_FLAG = 'dsa_layer_state';
export const DSA_LAYER_CONTROL_FLAG = 'dsa_layer_control';
export const DSA_LAYER_DATASET_ID_KEY = 'dsa_dataset_id';
export const DSA_LAYER_LIST_KEY = 'dsa_layers';
export const DSA_GEO_MEDIA_MARKERS_KEY = 'dsa_geo_media_markers';
export const DSA_LAYER_SETTINGS_KEY = 'dsa_layer_settings';
export const DSA_LAYER_ID_KEY = 'dsa_layer_id';
export const DSA_LAYER_OPACITY_KEY = 'dsa_layer_opacity';
export const DSA_LAYER_VISIBLE_KEY = 'dsa_layer_visible';
export const DSA_LAYER_COUNTS_STATE_FLAG = 'dsa_layer_counts_state';
export const DSA_LAYER_COUNTS_KEY = 'dsa_layer_counts';

// Trivia Control Flags (Remote Controller / future AI MC → Renderer)
export const TRIVIA_CONTROL_FLAG = 'trivia_control';
export const TRIVIA_COMMAND_KEY = 'trivia_command';
// Trivia authoritative game state (Renderer → controllers / kiosk / future AI observer).
// The renderer is the single source of truth; clients render this and never hold their own.
export const TRIVIA_STATE_FLAG = 'trivia_state';
export const TRIVIA_STATE_KEY = 'trivia';

// Sphere Slicing Control Flags
export const SLICE_CONTROL_FLAG = 'slice_control';
export const SLICE_COUNT_KEY = 'slice_count';
export const SLICE_STATE_FLAG = 'slice_state';

// Kiosk multi-slice control (Exploration mode)
export const KIOSK_SLICE_CONTROL_FLAG = 'kiosk_slice_control';
export const KIOSK_SLICE_STATE_FLAG = 'kiosk_slice_state';
export const KIOSK_SLICE_COMMAND_KEY = 'kiosk_slice_command';
export const KIOSK_SLICE_COMMAND_SELECT_DATASET = 'select_dataset';
// Audience-safe per-slice playback/overlay/view commands (Exploration mode).
export const KIOSK_SLICE_COMMAND_PLAY = 'play';
export const KIOSK_SLICE_COMMAND_PAUSE = 'pause';
export const KIOSK_SLICE_COMMAND_STOP = 'stop';
export const KIOSK_SLICE_COMMAND_SEEK = 'seek';
export const KIOSK_SLICE_COMMAND_VOLUME = 'volume';
export const KIOSK_SLICE_COMMAND_SPEED = 'speed';
export const KIOSK_SLICE_COMMAND_VIEW = 'view';
export const KIOSK_SLICE_COMMAND_OVERLAYS = 'overlays';
export const KIOSK_SLICE_COMMAND_RT_OVERLAY = 'rt_overlay';
// Server -> renderer only: an idle slice goes back to the presenter's content.
export const KIOSK_SLICE_COMMAND_CLEAR = 'clear';
export const KIOSK_SLICE_INDEX_KEY = 'kiosk_slice_index';
export const KIOSK_SLICE_DATASET_ID_KEY = 'kiosk_slice_dataset_id';
// The `overlays` command's layer ids (one basemap, still-image overlays; see
// shared/kiosk-map-layers), and the ids the slice draws in its slice state.
export const KIOSK_SLICE_OVERLAY_IDS_KEY = 'kiosk_slice_overlay_ids';

// Kiosk collaboration mode. `exploration`/`game` are the existing runtime
// modes; `presentation`/`cooperative`/`autoplay` are the configurable modes
// from the kiosk config model (see src/shared/kiosk-config.ts).
export const KIOSK_MODE_CONTROL_FLAG = 'kiosk_mode_control';
export const KIOSK_MODE_STATE_FLAG = 'kiosk_mode_state';
export const KIOSK_MODE_KEY = 'kiosk_mode';
export const KIOSK_MODE_EXPLORATION = 'exploration';
export const KIOSK_MODE_GAME = 'game';
export const KIOSK_MODE_PRESENTATION = 'presentation';
export const KIOSK_MODE_COOPERATIVE = 'cooperative';
export const KIOSK_MODE_AUTOPLAY = 'autoplay';

// Kiosk configuration + persistent identity (superuser-owned config broadcast
// to kiosks; kiosk registration by stable ID; locked-state reporting).
export const KIOSK_CONFIG_CONTROL_FLAG = 'kiosk_config_control';
export const KIOSK_CONFIG_STATE_FLAG = 'kiosk_config_state';
export const KIOSK_CONFIG_KEY = 'kiosk_config';
export const KIOSK_ID_KEY = 'kiosk_id';
export const KIOSK_LABEL_KEY = 'kiosk_label';
export const KIOSK_COUNT_KEY = 'kiosk_count';
export const KIOSK_DEFAULT_DATASET_ID_KEY = 'kiosk_default_dataset_id';
export const KIOSK_IDLE_TIMEOUT_SECONDS_KEY = 'kiosk_idle_timeout_seconds';
export const KIOSK_ASSIGNMENT_STATUS_KEY = 'kiosk_assignment_status';
export const KIOSK_LOCK_REASON_KEY = 'kiosk_lock_reason';
/** A kiosk's `status` asking the server to check its setup PIN. */
export const KIOSK_SETUP_VERIFY_STATUS = 'verify_setup_pin';
export const KIOSK_SETUP_PIN_KEY = 'kiosk_setup_pin';
/** The server's answer: `ok`, and when not, a `reason` and maybe `retryAfterMs`. */
export const KIOSK_SETUP_RESULT_FLAG = 'kiosk_setup_result';
export const KIOSK_CONTROL_ALLOWED_KEY = 'kiosk_control_allowed';

// Weekly Scheduler Flags (operator edits → scheduler service; state → all UIs)
export const SCHEDULE_CONTROL_FLAG = 'schedule_control';
export const SCHEDULE_STATE_FLAG = 'schedule_state';
export const SCHEDULE_COMMAND_KEY = 'schedule_command';
export const SCHEDULE_ENTRIES_KEY = 'schedule_entries';
export const SCHEDULE_NEXT_UP_KEY = 'schedule_next_up';
export const SCHEDULE_ACTIVE_ENTRY_ID_KEY = 'schedule_active_entry_id';
export const SCHEDULE_MASTER_ENABLED_KEY = 'schedule_master_enabled';
export const SCHEDULE_TRIVIA_ESTIMATE_FLAG = 'schedule_trivia_estimate';

// State Request Flags
export const STATE_REQUEST_FLAG = 'state_request';
export const MEDIA_INFO_REQUEST_FLAG = 'media_info_request';
export const REMOTE_STATE_FLAG = 'remote_state';

// Connection Management Flags
export const CONNECTION_HELLO_ACTION = 'hello';
export const CONNECTION_PING_ACTION = 'ping';

// Message Type Constants
export const MESSAGE_TYPE_CONTROL = 'control';
export const MESSAGE_TYPE_STATE = 'state';
export const MESSAGE_TYPE_MEDIA = 'media';
export const MESSAGE_TYPE_ERROR = 'error';

// Zoom Control Constants
export const MIN_ZOOM_VALUE = 0.1;
export const MAX_ZOOM_VALUE = 10.0;
export const DEFAULT_ZOOM_VALUE = 1.0;

// View Center Constraints
export const MIN_LONGITUDE_DEG = -180;
export const MAX_LONGITUDE_DEG = 180;
export const MIN_LATITUDE_DEG = -90;
// View pitch may cross one pole and invert the world, but stops before a
// second vertical revolution. Geographic latitude remains [-90, 90].
export const MIN_VIEW_LATITUDE_DEG = -180;
export const MAX_VIEW_LATITUDE_DEG = 180;

// ---- Presenter Position / View ----
export const USER_AZIMUTH_DEG_KEY = 'userAzimuthDeg';   // float [-180,180]
export const VIEW_STATE_FLAG = 'viewState';             // state packet section key
export const PLAYBACK_STATE_FLAG = 'playbackState';     // state packet section key
export const AUDIO_STATE_FLAG = 'audioState';           // state packet section key

// Optional: if you want commands with effective server time (sync-friendly)
export const EFFECTIVE_AT_KEY = 'effectiveAt';          // serverTime seconds (float)
export const PLAY_AT_KEY = 'playAt';                    // serverTime seconds (float)
export const MAX_LATITUDE_DEG = 90;

// ---- Dual-Canvas Streaming ----
/** Enable dual-canvas architecture: low-res preview + high-res stream */
export const DUAL_CANVAS_STREAMING_ENABLED = true;
/**
 * Stream canvas width (height is always width/2 for 2:1 equirectangular).
 * Options: 1024, 2048, 3072, 4096
 */
export const STREAM_RESOLUTION_WIDTH = 4096;

